/* @flow */

import type {MapCentralPin} from './Map';

export type MarkerSymbol = {
    anchor?: Object,
    path?: string,
    fillColor?: string,
    fillOpacity?: number,
    strokeColor?: string,
    strokeOpacity?: number,
    strokeWeight?: number,
    rotation?: number,
    scale?: number
};

const API_KEY = 'AIzaSyAKfp7tWpyTXK118mjWDcVLxtHxEyLinjM';
const MAP_VERSION = 'quarterly';

const MAP_PIN_COLORS = {
    homeawayPrimary: '#0067db',
    homeawayPrimaryDarker: '#004DA5', // 21% darken
    homeawaySecondary: '#ff8a00',
    homeawaySecondaryDarker: '#D96400', // 15% darken
    homelidaysPrimary: '#f80',
    homelidaysPrimaryDarker: '#D20', // 15% darken
    homelidaysSecondary: '#353e44',
    homelidaysSecondaryDarker: '#0F181E', // 15% darken
    odhrPrimary: '#ff9900',
    odhrPrimaryDarker: '#D97300', // 15% darken
    odhrSecondary: '#353e44',
    odhrSecondaryDarker: '#0F181E', // 15% darken
    vrboPrimary: '#1d924c',
    vrboPrimaryDarker: '#006C26', // 15% darken
    vrboSecondary: '#ffc033',
    vrboSecondaryDarker: '#D99A0D' // 15% darken
};

/**
 * Generate the Google Maps URL to fetch the library.
 * Note: channel is for tracking usage, do not change it without management approval
 * https://developers.google.com/maps/premium/reports/usage-reports#channels
 * @param {*} locale
 * @param {*} siteName
 * @return string
 */
export const getGoogleMapURL = (locale: string, siteName: string): string =>
    `https://maps.googleapis.com/maps/api/js?v=${MAP_VERSION}&channel=${siteName}_serp&libraries=geometry&key=${API_KEY}&language=${locale}&region=${locale.substring(
        3,
        5
    )}`;

/**
 * Generates map icon SVG
 * @param {string} siteName
 */
export const iconSymbol = (
    siteName: string,
    highlighted: boolean = false,
    hasVisited: boolean = false
): MarkerSymbol => {
    let colorKeyBrand = 'homeaway';
    let colorKeyImportance = highlighted ? 'Secondary' : 'Primary';
    let colorKeyModifier = hasVisited ? 'Darker' : '';

    if (siteName.includes('homelidays')) {
        colorKeyBrand = 'homelidays';
    } else if (siteName.includes('vrbo')) {
        colorKeyBrand = 'vrbo';
    } else if (siteName.includes('odhr')) {
        colorKeyBrand = 'odhr';
    }

    let fillColor =
        MAP_PIN_COLORS[
            `${colorKeyBrand}${colorKeyImportance}${colorKeyModifier}`
        ];

    return {
        /**
         * When changing the path, the anchor point need to be updated to the bottom center of the svg point.
         * https://developers.google.com/maps/documentation/javascript/symbols
         */
        anchor: new google.maps.Point(27, 32),
        path:
            'M 26.93,32.18 C 26.37,31.37 25.82,30.54 25.29,29.72 24.03,27.79 22.82,25.82 21.68,23.83 21.01,22.67 20.37,21.49 19.77,20.30 17.68,16.26 16.50,13.07 16.50,11.10 16.50,5.24 21.24,0.50 27.10,0.50 32.95,0.50 37.69,5.24 37.69,11.10 37.69,13.07 36.51,16.26 34.43,20.33 33.81,21.52 33.16,22.70 32.49,23.82 31.35,25.82 30.12,27.81 28.89,29.71 28.46,30.37 28.06,30.97 27.70,31.51 27.70,31.51 27.25,32.17 27.25,32.17 27.13,32.34 27.06,32.34 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.93,32.18 26.93,32.18 Z M 26.93,32.18 C 26.37,31.37 25.82,30.54 25.29,29.72 24.03,27.79 22.82,25.82 21.68,23.83 21.01,22.67 20.37,21.49 19.77,20.30 17.68,16.26 16.50,13.07 16.50,11.10 16.50,5.24 21.24,0.50 27.10,0.50 32.95,0.50 37.69,5.24 37.69,11.10 37.69,13.07 36.51,16.26 34.43,20.33 33.81,21.52 33.16,22.70 32.49,23.82 31.35,25.82 30.12,27.81 28.89,29.71 28.46,30.37 28.06,30.97 27.70,31.51 27.70,31.51 27.25,32.17 27.25,32.17 27.13,32.34 27.06,32.34 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.95,32.18 26.93,32.18 26.93,32.18 Z',
        fillColor,
        fillOpacity: 1,
        strokeColor: '#FFF'
    };
};

/**
 * Generates the icon SVG for the `mapCentralPin` which is used to mark cities and POIs related to the user search.
 */
export const mapCentralPinSymbol = (): MarkerSymbol => {
    return {
        /**
         * When changning the path, the anchor point need to be updated to the bottom center of the svg point.
         * https://developers.google.com/maps/documentation/javascript/symbols
         */
        anchor: new google.maps.Point(27, 32),
        path:
            'M24.766 8.315C24.766 3.73 21.006 0 16.383 0 11.76 0 8 3.73 8 8.315c0 4.272 3.267 7.799 7.452 8.26v15.76h1.863v-15.76c4.185-.461 7.451-3.988 7.451-8.26z',
        fillColor: '#323F4D',
        fillOpacity: 1,
        strokeColor: '#323F4D'
    };
};

export const VALID_GEOTYPES = [
    'locality',
    'poi',
    'point_of_interest',
    'airport',
    'event',
    'train_station',
    'metro_station'
];

/**
 * Checks whether to show map central pin
 * @param {MapCentralPin} mapCentralPin
 */
export const shouldShowMapCentralPin = (
    mapCentralPin?: MapCentralPin = {}
): boolean => {
    const {lat, lng, geoType, isGeoCoded} = mapCentralPin;

    // If there is a defined center pin Lat/Lng and valid geoType.
    // If geoType is not defined or not valid, check whether Google Geocoder was used (for address searches)
    return !!lat && !!lng && (VALID_GEOTYPES.includes(geoType) || !!isGeoCoded);
};
